/**
 * @voltxampere/presets — Wien-bridge oscillator (Sprint 50-4)
 *
 * Classic Wien-bridge sinusoidal oscillator. Op-amp non-inverting
 * configuration with feedback through Wien network (R-C series in
 * one leg, R||C parallel in the other). Oscillation frequency
 * f = 1/(2πRC). With R=10kΩ + C=10nF → f ≈ 1.6 kHz.
 *
 * Pedagogical: shows positive feedback + Barkhausen criterion.
 * Real builds need amplitude stabilization (lamp / JFET / clamp).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const wienBridgeOsc = {
    id: "wien-bridge-osc",
    name: { tr: "Wien Köprüsü Osilatörü", en: "Wien-Bridge Oscillator" },
    category: "oscillator",
    difficulty: 4,
    description: {
        tr: "Op-amp + Wien ağı ile sinüs osilatörü. f = 1/(2πRC) ≈ 1.6 kHz.",
        en: "Op-amp + Wien network sine oscillator. f = 1/(2πRC) ≈ 1.6 kHz.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "Wien-Bridge Oscillator",
        description: "Op-amp Wien-bridge sine oscillator",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "v_p" }),
            makeNet({ name: "v_n" }),
            makeNet({ name: "v_out" }),
        ],
        components: [
            makeComponent({
                id: "V_CC",
                type: "voltage-source",
                params: { voltage: 12 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "V_CC",
                position: { x: -240, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "U1",
                type: "opamp",
                params: { gain: 1e5, vSat: 11 },
                terminals: { in_pos: "net_v_p", in_neg: "net_v_n", out: "net_v_out" },
                label: "U1",
                position: { x: 0, y: 0 },
                rotation: 0,
            }),
            // Wien network: R-C series (v_out → v_p)
            makeComponent({
                id: "R_S",
                type: "resistor",
                params: { resistance: 10000 },
                terminals: { p1: "net_v_out", p2: "net_v_p" },
                label: "R_S",
                position: { x: 80, y: 80 },
                rotation: 0,
            }),
            makeComponent({
                id: "C_S",
                type: "capacitor",
                params: { capacitance: 10e-9 },
                terminals: { p1: "net_v_out", p2: "net_v_p" },
                label: "C_S",
                position: { x: 80, y: 120 },
                rotation: 0,
            }),
            // Wien network: R||C parallel (v_p → gnd)
            makeComponent({
                id: "R_P",
                type: "resistor",
                params: { resistance: 10000 },
                terminals: { p1: "net_v_p", p2: "net_gnd" },
                label: "R_P",
                position: { x: -80, y: 80 },
                rotation: 90,
            }),
            makeComponent({
                id: "C_P",
                type: "capacitor",
                params: { capacitance: 10e-9 },
                terminals: { p1: "net_v_p", p2: "net_gnd" },
                label: "C_P",
                position: { x: -120, y: 80 },
                rotation: 90,
            }),
            // Feedback divider: 2× gain (Rf/Rg = 2)
            makeComponent({
                id: "R_F",
                type: "resistor",
                params: { resistance: 20000 },
                terminals: { p1: "net_v_out", p2: "net_v_n" },
                label: "R_F",
                position: { x: 80, y: -80 },
                rotation: 0,
            }),
            makeComponent({
                id: "R_G",
                type: "resistor",
                params: { resistance: 10000 },
                terminals: { p1: "net_v_n", p2: "net_gnd" },
                label: "R_G",
                position: { x: -80, y: -80 },
                rotation: 0,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -240, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [],
    }),
    expectedValues: [
        {
            node: "v_out",
            analysis: "dc",
            value: 0,
            tolerance: 1.0,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Pozitif geri besleme + Barkhausen kriteri.",
            en: "Positive feedback + Barkhausen criterion.",
        },
        {
            tr: "f = 1/(2πRC) Wien ağ frekansı.",
            en: "Wien network resonant frequency f = 1/(2πRC).",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 17"],
};
//# sourceMappingURL=wien-bridge-osc.js.map